import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { SidebarNavItem } from '@/components/layout/app-shell'
import { ErrorState } from '@/components/feedback/error-state'
import { Skeleton } from '@/components/feedback/skeleton'
import { MobilePageHeader } from '@/components/mobile/page-header'
import { Button } from '@/components/primitives/button'
import { useHubAgentName } from '@/features/shared/brand/branding-hooks'
import { readSearchString } from '@/features/shared/chat/utils'
import { useCoreAgentStatus } from '@/features/user/core-agent/use-status'
import { AppMarketIcon } from '@/features/user/shell/navigation-icons'
import { useSidebarInstalledApps } from '@/features/user/shell/navigation'
import { UserShellPage } from '@/features/user/shell/shell-page'
import { useMediaQuery } from '@/lib/use-media-query'

const LazyChatPage = lazy(() =>
  import('@/features/user/chat/chat-page').then((module) => ({ default: module.ChatPage })),
)
const LazyCoreAgentChatPage = lazy(() =>
  import('@/features/user/core-agent/chat-page').then((module) => ({
    default: module.CoreAgentChatPage,
  })),
)

export function ChatRoutePage() {
  // 离开 /chat 时组件尚未卸载，读取本路由的 search，避免目标页参数触发默认应用跳转。
  const search: { appId?: unknown } = useSearch({ strict: false })
  const appId = readSearchString(search.appId)

  // appId 是显式目标，不能被 Hub Agent 状态查询阻断或改写。
  if (appId) {
    return (
      <Suspense fallback={<ChatRouteLoading />}>
        <LazyChatPage />
      </Suspense>
    )
  }

  return <DefaultChatRoute />
}

function ChatRouteLoading() {
  return (
    <ChatRouteFallbackShell>
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-[800px] space-y-4">
          <Skeleton className="mx-auto h-8 w-72 rounded-full" />
          <Skeleton className="h-28 rounded-[22px]" />
        </div>
      </div>
    </ChatRouteFallbackShell>
  )
}

function ChatRouteFallbackShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('shell')
  // shell 不再提供统一 header,移动端加载/错误屏也要能打开抽屉。
  const isMobile = useMediaQuery('(max-width: 767px)')
  const navigation: SidebarNavItem[] = [
    {
      icon: <AppMarketIcon />,
      key: 'app-market',
      label: t('userNavigation.appMarket'),
      path: '/apps',
      sticky: 'top',
      variant: 'ghost-prominent',
    },
  ]

  return (
    <UserShellPage activePath="/chat" navigation={navigation}>
      <div className="flex h-full flex-col overflow-hidden">
        {isMobile ? <MobilePageHeader title="" /> : null}
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </UserShellPage>
  )
}

function DefaultChatRoute() {
  const { t } = useTranslation('chat')
  const hubAgentName = useHubAgentName()
  const statusQuery = useCoreAgentStatus()

  if (statusQuery.isError) {
    return (
      <ChatRouteFallbackShell>
        <div className="flex h-full items-center justify-center px-6">
          <ErrorState
            action={
              <Button
                className="mt-3"
                onClick={() => {
                  void statusQuery.refetch()
                }}
                size="sm"
                variant="secondary"
              >
                {t('errors.retry')}
              </Button>
            }
            description={t('coreAgent.statusErrorDescription', { hubAgentName })}
            title={t('coreAgent.statusErrorTitle', { hubAgentName })}
          />
        </div>
      </ChatRouteFallbackShell>
    )
  }

  if (statusQuery.isLoading || !statusQuery.data) {
    return <ChatRouteLoading />
  }

  if (statusQuery.data.enabled) {
    return (
      <Suspense fallback={<ChatRouteLoading />}>
        <LazyCoreAgentChatPage />
      </Suspense>
    )
  }

  return <DisabledHubAgentRoute />
}

function DisabledHubAgentRoute() {
  const { t } = useTranslation('chat')
  const navigate = useNavigate()
  const installedApps = useSidebarInstalledApps()
  // 跳转目标取「我的应用」页面顺序的首个应用，而非侧栏拼音顺序。
  const targetAppId = installedApps.myAppsOrder[0]?.id

  useEffect(() => {
    if (!installedApps.ready || installedApps.loading || installedApps.error) {
      return
    }

    if (targetAppId) {
      void navigate({
        replace: true,
        search: { appId: targetAppId } as never,
        to: '/chat',
      })
      return
    }

    void navigate({ replace: true, to: '/apps' })
  }, [installedApps.error, installedApps.loading, installedApps.ready, navigate, targetAppId])

  if (installedApps.error) {
    return (
      <ChatRouteFallbackShell>
        <div className="flex h-full items-center justify-center px-6">
          <ErrorState
            action={
              <Button
                className="mt-3"
                onClick={() => {
                  void installedApps.refetch()
                }}
                size="sm"
                variant="secondary"
              >
                {t('errors.retry')}
              </Button>
            }
            description={t('coreAgent.appsErrorDescription')}
            title={t('coreAgent.appsErrorTitle')}
          />
        </div>
      </ChatRouteFallbackShell>
    )
  }

  return <ChatRouteLoading />
}
